import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import utils, { safeStringify } from 'utils';

const isKeyOfWindow = (key: string | keyof Window): key is keyof Window =>
  !!window[key as keyof Window];

export default function useSiteInitializedState<T>(
  stateKey: string,
  alternateDefault?: T,
) {
  const script = useRef<HTMLScriptElement>();
  const [state, setter] = useState<T | undefined>(
    isKeyOfWindow(stateKey)
      ? (window[stateKey] as unknown as T)
      : alternateDefault,
  );

  const scriptId = useMemo(() => `${stateKey}-site-data`, [stateKey]);

  const setState = useCallback(
    (newState) => {
      setter((old) => {
        const newVal =
          typeof newState === 'function' ? newState(old) : newState;

        if (script.current) {
          document.body.removeChild(script.current);
          script.current.innerHTML = `window["${stateKey}"]=${safeStringify(
            newVal,
          )};`;
          document.body.appendChild(script.current);
        }

        return newVal;
      });
    },
    [stateKey],
  );

  useEffect(() => {
    if (!utils.isSSG()) {
      const script = document.getElementById(scriptId);
      if (script) {
        document.body.removeChild(script);
      }

      return;
    }

    script.current = document.createElement('script');
    script.current.id = scriptId;
    document.body.appendChild(script.current);
  }, [scriptId]);

  return [state, setState] as [T, React.Dispatch<React.SetStateAction<T>>];
}
